// Themes studio: theme profiles as pure builders. A profile is a recipe, applying it copies it
// into the wearer's own look (lib/look.ts `withStudio`), so nothing here can reach another person.
// The writes themselves (create, patch, delete, apply) are in ./actions.ts.
import {
  NAME_MAX,
  OWNER_AUTHOR,
  PROFILE_AUTHOR_LABEL,
  THEME_SPEC_V,
  profileSpec,
  type ProfileSpec,
  type ThemeSpec,
} from '@/kinds/themeprofile/schema'
import type { ThemeProfileResource } from '@/kinds/themeprofile/apply'

/** A change to a ThemeSpec by section: a section given replaces its own keys (null removes the section). */
export type ThemePatch = {
  [K in Exclude<keyof ThemeSpec, 'v'>]?: ThemeSpec[K] | null
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * `patch` merged into `base`: a section (accent, shape, glass…) is merged key by key, `modes`
 * mode by mode; null (or undefined values inside a section) removes it. Nothing else changes.
 */
export const mergeTheme = (base: ThemeSpec, patch: ThemePatch): ThemeSpec => {
  const out: Record<string, unknown> = { ...base }
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) delete out[k]
    else if (v === undefined) continue
    else if (k === 'modes' && isObj(v)) {
      const modes: Record<string, unknown> = { ...(isObj(out.modes) ? out.modes : {}) }
      for (const [m, mv] of Object.entries(v)) {
        if (mv === null || mv === undefined) delete modes[m]
        else modes[m] = mv
      }
      if (Object.keys(modes).length) out.modes = modes
      else delete out.modes
    } else if (isObj(v) && isObj(out[k])) out[k] = dropUndefined({ ...(out[k] as object), ...v })
    else out[k] = isObj(v) ? dropUndefined(v) : v
  }
  return out as ThemeSpec
}

const dropUndefined = (o: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null))

/** Reset one section of a theme to its default (the studio's "Reset section"). */
export const resetSection = (theme: ThemeSpec, section: Exclude<keyof ThemeSpec, 'v'>): ThemeSpec => {
  const out = { ...theme }
  delete out[section]
  return out
}

export interface NewProfile {
  name: string
  emoji?: string
  theme?: ThemeSpec
  visibility?: ProfileSpec['visibility']
  /** the author's display name; the `author` label is the authority and is the server's */
  authorName?: string
  copiedFrom?: string
}

const clean = (name: string) => [...name.trim()].slice(0, NAME_MAX).join('')

/** The spec of a new profile. */
export const newProfileSpec = (p: NewProfile): ProfileSpec => ({
  name: clean(p.name) || 'Theme',
  emoji: p.emoji || '🎨',
  author_name: (p.authorName ?? '').slice(0, 40),
  visibility: p.visibility ?? 'workspace',
  ...(p.copiedFrom ? { copied_from: p.copiedFrom } : {}),
  theme: p.theme ?? { v: THEME_SPEC_V },
})

/** The labels a create carries: the suite it is shared in, and the author (`owner` or `grant:<id>`). */
export const profileLabels = (ws: string | undefined, author: string): Record<string, string> => ({
  ...(ws ? { ws } : {}),
  [PROFILE_AUTHOR_LABEL]: author,
})

/** A patch to a profile's spec: only the keys given; `theme` is a ThemePatch merged into the current one. */
export interface ProfilePatch {
  name?: string
  emoji?: string
  visibility?: ProfileSpec['visibility']
  theme?: ThemePatch
}

/** The spec patch (what `mutate.patch` sends) for a change to `current`: only what differs. */
export const profilePatch = (current: ProfileSpec, change: ProfilePatch): Partial<ProfileSpec> => {
  const out: Partial<ProfileSpec> = {}
  if (change.name !== undefined && clean(change.name) && clean(change.name) !== current.name)
    out.name = clean(change.name)
  if (change.emoji !== undefined && change.emoji && change.emoji !== current.emoji) out.emoji = change.emoji
  if (change.visibility !== undefined && change.visibility !== current.visibility)
    out.visibility = change.visibility
  if (change.theme) {
    const next = mergeTheme(current.theme, change.theme)
    if (JSON.stringify(next) !== JSON.stringify(current.theme)) out.theme = next
  }
  return out
}

/** A copy of someone's profile as the copier's own: new name ("… copy" unless renamed), provenance kept. */
export const duplicateSpec = (
  src: ThemeProfileResource,
  copier: { authorName?: string; name?: string },
): ProfileSpec =>
  newProfileSpec({
    name: copier.name ?? `${src.spec.name} copy`,
    emoji: src.spec.emoji,
    theme: src.spec.theme,
    visibility: 'workspace',
    authorName: copier.authorName,
    copiedFrom: src.id,
  })

/** Whose is it: the author key on its labels. */
export const authorOf = (r: Pick<ThemeProfileResource, 'labels'>): string =>
  r.labels[PROFILE_AUTHOR_LABEL] ?? OWNER_AUTHOR

/** May `me` (`owner` or `grant:<id>`) edit or delete this profile? The server enforces it; this hides the buttons. */
export const mayEdit = (r: Pick<ThemeProfileResource, 'labels'>, me: string): boolean => authorOf(r) === me

/** Mine first, then the others', each newest first; private ones of other people never listed. */
export const galleryOrder = (list: ThemeProfileResource[], me: string): ThemeProfileResource[] =>
  list
    .filter((r) => !r.deleted_at && (r.spec.visibility === 'workspace' || authorOf(r) === me))
    .sort((a, b) => {
      const am = authorOf(a) === me
      const bm = authorOf(b) === me
      if (am !== bm) return am ? -1 : 1
      return b.updated_at.localeCompare(a.updated_at)
    })

/** A profile spec is valid for the server: the same zod the kind uses. */
export const profileIssues = (spec: unknown): string[] => {
  const r = profileSpec.safeParse(spec)
  return r.success ? [] : r.error.issues.map((i) => `${i.path.join('.') || 'spec'}: ${i.message}`)
}

/** The four swatches a card shows: background, raised, accent, text. */
export const swatchOf = (
  theme: ThemeSpec,
): { background?: string; raised?: string; accent?: string; highlight?: string; text?: string } => {
  const m = theme.modes?.dark ?? theme.modes?.black
  return {
    background: m?.surfaces?.background,
    raised: m?.surfaces?.raised,
    accent: m?.accent?.colour ?? theme.accent?.colour,
    highlight: m?.accent?.highlight ?? theme.accent?.highlight,
    text: m?.surfaces?.text,
  }
}
